import { apiClient } from './apiClient'

export interface UserOrganization {
  id: number
  name: string
}

export interface AuthUser {
  id: number
  email: string
  name: string
  organization: UserOrganization
  isAdmin?: boolean
}

interface AuthResponse {
  success: boolean
  data?: AuthUser
  error?: string
}

/**
 * Attempt to log in. Resolves to the user record on success; throws an
 * `Error` whose `.message` is suitable for displaying to the user on
 * failure. Network/server errors are also surfaced as Error.
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/login', {
      email,
      password,
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Login fejlede')
    }
    return response.data.data
  } catch (err: any) {
    if (err.response?.data?.error) {
      throw new Error(err.response.data.error)
    }
    if (err.message) throw err
    throw new Error('Ukendt fejl ved login')
  }
}

/**
 * Best-effort logout. Always resolves — server-side session is destroyed
 * if reachable; the caller should clear its local state regardless.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout')
  } catch {
    // Intentionally swallow — local state will be cleared by caller.
  }
}

/**
 * Return the current user if a valid session exists, otherwise null.
 * 401 is treated as "not logged in" (the normal unauthenticated case).
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await apiClient.get<AuthResponse>('/auth/me')
    return response.data.data ?? null
  } catch (err: any) {
    if (err.response?.status === 401) return null
    throw err
  }
}
